import React, { useEffect, useRef, useState } from 'react';
import { Box, Chip, Tab, Tabs } from '@mui/material';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import { bool, func, oneOf, shape } from 'prop-types';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';
import CarriersTab from './CarriersTab';
import LcnsTab from './LcnsTab';
import ProductsTab from './products/ProductsTab';
import TerminalsTab from './terminals/TerminalsTab';

const TAB_ITEMS = [
  { countKey: 'terminalsCount', label: 'Terminals', value: 'terminals' },
  { countKey: 'productsCount', label: 'Products', value: 'products' },
  {
    countKey: 'loadingControlNumbersCount',
    editCountKey: 'controlNumbersCount',
    label: 'LCNs',
    value: 'lcns',
  },
  { countKey: 'carriersCount', label: 'Carriers', value: 'carriers' },
];

const TAB_CONTENT = {
  carriers: CarriersTab,
  lcns: LcnsTab,
  products: ProductsTab,
  terminals: TerminalsTab,
};

const getTabCount = (customerAuth, tab, isEditMode) => {
  if (tab.value === 'products' && customerAuth.authorizationType !== 'SPECIFIC') {
    return 'All';
  }
  // Products can be added/removed locally before saving, so count the live array.
  if (tab.value === 'products' && Array.isArray(customerAuth?.products)) {
    return customerAuth.products.length;
  }
  // Terminals can be added/removed locally before saving, so count the live array
  // instead of the server-provided terminalsCount, which only updates after a save.
  if (tab.value === 'terminals' && Array.isArray(customerAuth?.terminals)) {
    return customerAuth.terminals.length;
  }

  const countKey = isEditMode && tab.editCountKey ? tab.editCountKey : tab.countKey;
  const count = customerAuth?.[countKey];
  if (count !== undefined && count !== null) {
    return count;
  }

  return 0;
};

const CustomerAuthorizationTabs = ({
  customerAuth,
  hasProductChanges,
  hasTerminalChanges,
  mode,
  productsError,
  setCustomerAuth,
  setValidation,
  isEditMode,
  terminalsError,
  validation,
}) => {
  const [selectedTab, setSelectedTab] = useState('terminals');
  const [visitedTabs, setVisitedTabs] = useState(['terminals']);
  const authorizationKey = customerAuth?.customerAuthorizationId ?? customerAuth?.customerIdName;
  const previousAuthorizationKeyRef = useRef(authorizationKey);

  useEffect(() => {
    if (
      previousAuthorizationKeyRef.current &&
      previousAuthorizationKeyRef.current !== authorizationKey
    ) {
      setSelectedTab('terminals');
      setVisitedTabs(['terminals']);
    }
    previousAuthorizationKeyRef.current = authorizationKey;
  }, [authorizationKey]);

  const handleTabChange = (_, value) => {
    setSelectedTab(value);
    setVisitedTabs(previous => (previous.includes(value) ? previous : [...previous, value]));
  };

  const terminalsTabFlag = EvaluateFlag(
    process.env.REACT_APP_LD_CUSTOMER_AUTHORIZATION_TERMINALS_TAB
  );
  const productsTabFlag = EvaluateFlag(
    process.env.REACT_APP_LD_CUSTOMER_AUTHORIZATION_PRODUCTS_TAB
  );

  const renderTabLabel = value => {
    const tab = TAB_ITEMS.find(item => item.value === value);
    const count = getTabCount(customerAuth, tab, isEditMode);

    return (
      <Box sx={{ alignItems: 'center', display: 'inline-flex', gap: terminalsTabFlag ? 0.75 : 1 }}>
        <span>{tab?.label}</span>
        <Chip label={count} size="small" type="accordion" variant="badge" />
        {value === 'terminals' && terminalsError && (
          <ErrorRoundedIcon sx={{ color: 'error.main', fontSize: 16 }} />
        )}
        {value === 'terminals' && !terminalsError && hasTerminalChanges && (
          <Box
            aria-label="terminals edited"
            sx={{ backgroundColor: 'warning.main', borderRadius: '50%', height: 8, width: 8 }}
          />
        )}
        {productsTabFlag && value === 'products' && productsError && (
          <ErrorRoundedIcon sx={{ color: 'error.main', fontSize: 16 }} />
        )}
        {productsTabFlag && value === 'products' && !productsError && hasProductChanges && (
          <Box
            aria-label="products edited"
            sx={{ backgroundColor: 'warning.main', borderRadius: '50%', height: 8, width: 8 }}
          />
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Tabs
        onChange={handleTabChange}
        scrollButtons="auto"
        sx={{
          '& .MuiTabs-flexContainer': {
            gap: terminalsTabFlag ? 1 : 2,
            justifyContent: 'flex-start',
          },
          borderBottom: 1,
          borderColor: 'divider',
          mt: 2,
          ...(terminalsTabFlag && {
            '& .MuiTab-root': {
              alignItems: 'center',
              minHeight: 44,
              minWidth: 0,
              px: 1.5,
              textTransform: 'none',
            },
            borderBottom: '1px solid',
            borderColor: 'grey.200',
          }),
        }}
        value={selectedTab}
        variant="scrollable"
      >
        {TAB_ITEMS.map(tab => (
          <Tab key={tab.value} label={renderTabLabel(tab.value)} value={tab.value} />
        ))}
      </Tabs>
      <Box
        sx={{
          pt: 2,
        }}
      >
        {TAB_ITEMS.map(tab => {
          if (!visitedTabs.includes(tab.value)) {
            return null;
          }
          const TabContent = TAB_CONTENT[tab.value];
          const isSelected = selectedTab === tab.value;
          return (
            <Box key={tab.value} sx={{ display: isSelected ? 'block' : 'none' }}>
              <TabContent
                customerAuth={customerAuth}
                hasProductChanges={hasProductChanges}
                hasTerminalChanges={hasTerminalChanges}
                isEditMode={isEditMode}
                mode={mode}
                productsError={productsError}
                setCustomerAuth={setCustomerAuth}
                setValidation={setValidation}
                terminalsError={terminalsError}
                validation={validation}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

CustomerAuthorizationTabs.propTypes = {
  customerAuth: shape({}).isRequired,
  hasProductChanges: bool,
  hasTerminalChanges: bool,
  isEditMode: bool.isRequired,
  mode: oneOf(['edit', 'view']),
  productsError: bool,
  setCustomerAuth: func.isRequired,
  setValidation: func.isRequired,
  terminalsError: bool,
  validation: shape({}).isRequired,
};

CustomerAuthorizationTabs.defaultProps = {
  hasProductChanges: false,
  hasTerminalChanges: false,
  mode: 'edit',
  productsError: false,
  terminalsError: false,
};

export default CustomerAuthorizationTabs;
